import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NmibleCountries } from '../../../../../core/constants/nmible-countries';
import { Permissions } from '../../../../../core/constants/permissions';
import { LogHelper } from '../../../../../core/helpers/log.helper';
import {
  CreateNmibleBankAccountRequest,
  FullPatientPaymentInfo,
  NmibleBankAccountDetailsResponse,
  NmibleBankAccountSummary,
  NmibleGetPaymentMethodsResponse,
  NmiblePaymentMethodProperty,
  NmiblePaymentMethodSubProperty,
  NmiblePaymentOption,
  NmiblePaymentOptionType
} from '../../../../../core/models/patient.model';
import { SelectOption } from '../../../../../core/models/select-option.model';
import { AuthService } from '../../../../../core/services/auth.service';
import { PatientService } from '../../../../../core/services/patient.service';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { ConfirmActionModalComponent } from '../../../../../shared/confirm-action-modal/confirm-action-modal.component';
import { ModalComponent } from '../../../../../shared/modal/modal.component';
import { ModalSize } from '../../../../../shared/modal/modal-size.enum';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-payment-info-nmible-bank',
  templateUrl: './payment-info-nmible-bank.component.html',
  styleUrls: ['./payment-info-nmible-bank.component.scss']
})
export class PaymentInfoNmibleBankComponent implements OnInit, OnDestroy
{
  private destroy$ = new Subject<void>();
  private datePickersRebuilt$ = new Subject<void>();

  @ViewChild('assignBankDetailsModal') assignBankDetailsModal: ModalComponent;
  @ViewChild('viewBankDetailsModal') viewBankDetailsModal: ModalComponent;
  @ViewChild('confirmRemoveBankAccountModal') confirmRemoveBankAccountModal: ConfirmActionModalComponent;

  @Input() patientDetail: FullPatientPaymentInfo;
  @Input() patientTrialId: string;

  Permissions = Permissions;
  ModalSize = ModalSize;
  canWritePaymentInfo = false;
  loadingMethods = false;
  processing = false;

  loadingBankAccounts = false;
  bankAccounts: NmiblePaymentOption[] = [];

  loadingSourceAccounts = false;
  sourcePatientName = '';
  sourceAccounts: NmibleBankAccountSummary[] = [];
  selectedSourceAccount: NmibleBankAccountSummary | null = null;
  sourceBankDetails: NmibleBankAccountDetailsResponse | null = null;
  sourceIdentityFields: { [key: string]: any } = {};
  sourceAccountFields: { [key: string]: any } = {};
  sourceBankMethod: NmibleGetPaymentMethodsResponse | null = null;

  viewedBankAccount: NmiblePaymentOption | null = null;
  viewedIdentityFields: { [key: string]: any } = {};
  viewedAccountFields: { [key: string]: any } = {};
  viewedBankMethod: NmibleGetPaymentMethodsResponse | null = null;

  countries: SelectOption[] = NmibleCountries.all().map((x) => ({ value: x.code, text: x.name }));
  bankMethods: NmibleGetPaymentMethodsResponse[] = [];
  bankMethod: NmibleGetPaymentMethodsResponse | null = null;

  // Holds the Date value behind each dynamic field rendered as a date picker; the dynamic field
  // itself keeps the formatted string Nmible expects.
  datePickerGroup: FormGroup = new FormGroup({});
  datePickerPropertyNames = new Set<string>();
  dateOfBirthMin = new Date(1900, 0, 1);
  dateOfBirthMax = new Date();

  bankForm = new FormGroup({
    givenName: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z.' ]+(?<![.'])$/)]),
    middleName: new FormControl('', [Validators.pattern(/^[A-Za-z.' ]+(?<![.'])$/)]),
    familyName: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z.' ]+(?<![.'])$/)]),
    email: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/)]),
    country: new FormControl('', [Validators.required]),
    provider: new FormControl('', [Validators.required]),
    accountFields: new FormGroup({})
  });

  copySourceForm = new FormGroup({
    sourcePatientId: new FormControl(''),
    sourceTrialId: new FormControl('')
  });

  // Populated only when the source patient has more than one selected bank account (one per trial).
  sourceTrialOptions: SelectOption[] = [];

  get accountFieldsGroup(): FormGroup
  {
    return this.bankForm.get('accountFields') as FormGroup;
  }

  get bankFieldProperties(): NmiblePaymentMethodProperty[]
  {
    return this.bankMethod?.properties ?? [];
  }

  // propertyName is the stable Nmible-supplied key; the displayed title varies by country/provider
  // and can collide with unrelated fields (e.g. "Bank Name" also contains "name").
  private static readonly IDENTITY_PROPERTY_KIND: { [propertyName: string]: 'given' | 'middle' | 'family' | 'full' | 'email' } = {
    given_name: 'given',
    first_name: 'given',
    middle_name: 'middle',
    family_name: 'family',
    last_name: 'family',
    full_name: 'full',
    email: 'email'
  };

  private static readonly DATE_OF_BIRTH_PROPERTY_NAMES = ['date_of_birth', 'dateofbirth', 'dob', 'birth_date', 'birthdate'];

  private static readonly ISO_DATE_PROBE = '1985-06-15';

  // Pre-fills a dynamic field by matching the stable propertyName to the patient's own details.
  private getPrefillValue(prop: NmiblePaymentMethodProperty): string
  {
    const firstName = this.patientDetail?.firstName ?? '';
    const lastName = this.patientDetail?.lastName ?? '';

    switch (PaymentInfoNmibleBankComponent.IDENTITY_PROPERTY_KIND[prop.propertyName])
    {
      case 'given':
        return firstName;
      case 'middle':
        return '';
      case 'family':
        return lastName;
      case 'full':
        return [firstName, lastName].filter(Boolean).join(' ');
      case 'email':
        return this.patientDetail?.email ?? '';
      default:
        return prop.type === 'email' ? (this.patientDetail?.email ?? '') : '';
    }
  }

  constructor(
    public authService: AuthService,
    private patientService: PatientService,
    private alertService: AlertService
  ) { }

  ngOnInit()
  {
    this.canWritePaymentInfo = this.authService.hasPermission(Permissions.PatientPaymentInfoWrite);
    this.loadBankAccounts();
    this.setupCountryChangeListener();
    this.setupProviderChangeListener();

    if (!this.canWritePaymentInfo)
    {
      this.bankForm.disable();
    }
  }

  ngOnDestroy()
  {
    this.destroy$.next();
    this.destroy$.complete();
    this.datePickersRebuilt$.complete();
  }

  loadBankAccounts()
  {
    this.loadingBankAccounts = true;
    this.patientService.getNmiblePaymentOptions(this.patientTrialId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (options) =>
      {
        this.bankAccounts = options.filter(o => o.optionType === NmiblePaymentOptionType.BankAccount);
        this.loadingBankAccounts = false;
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.loadingBankAccounts = false;
        this.alertService.showWarningAlert('Failed to fetch bank accounts. Please try again or contact support.');
      }
    });
  }

  selectBankAccount(optionId: string)
  {
    this.processing = true;
    this.patientService.selectNmiblePaymentOption(this.patientTrialId, optionId).subscribe({
      next: () =>
      {
        this.processing = false;
        this.loadBankAccounts();
        this.alertService.showSuccessAlert('Bank account selected successfully');
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showWarningAlert('Failed to select bank account. Please try again or contact support.');
      }
    });
  }

  confirmRemoveBankAccount(optionId: string)
  {
    this.confirmRemoveBankAccountModal.showConfirmation(optionId, 'Remove Bank Account', 'Are you sure you want to remove this bank account?');
  }

  removeBankAccount(optionId: string)
  {
    this.processing = true;
    this.patientService.removeNmiblePaymentOption(this.patientTrialId, optionId).subscribe({
      next: () =>
      {
        this.processing = false;
        this.loadBankAccounts();
        this.alertService.showSuccessAlert('Bank account removed successfully');
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showWarningAlert('Failed to remove bank account. Please try again or contact support.');
      }
    });
  }

  viewBankAccount(account: NmiblePaymentOption)
  {
    this.viewedBankAccount = account;
    this.viewedBankMethod = null;

    const split = this.splitFields(account.fields ?? {});
    this.viewedIdentityFields = split.identityFields;
    this.viewedAccountFields = split.accountFields;

    this.resolveBankMethod(account.country, this.viewedIdentityFields['_provider']).pipe(takeUntil(this.destroy$)).subscribe({
      next: (method) =>
      {
        this.viewedBankMethod = method;
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.viewedBankMethod = null;
      }
    });

    this.viewBankDetailsModal?.show();
  }

  getViewFieldTitle(propertyName: string): string
  {
    return this.getFieldTitle(this.viewedBankMethod, propertyName);
  }

  closeViewBankDetails()
  {
    this.viewBankDetailsModal?.hide();
  }

  setupCountryChangeListener()
  {
    this.bankForm.get('country').valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((countryCode: string) =>
    {
      if (countryCode)
      {
        this.bankMethods = [];
        this.loadBankFields(countryCode);
      }
    });
  }

  setupProviderChangeListener()
  {
    this.bankForm.get('provider').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((providerName: string) =>
    {
      if (!providerName || !this.bankMethods.length)
      {
        return;
      }

      const method = this.bankMethods.find(m => m.provider === providerName);

      if (!method || method.provider === this.bankMethod?.provider)
      {
        return;
      }

      this.bankMethod = method;

      const group = this.accountFieldsGroup;
      Object.keys(group.controls).forEach(key => group.removeControl(key));
      this.buildAccountFields();
    });
  }

  private buildAccountFields()
  {
    const group = this.accountFieldsGroup;

    this.resetDatePickers();

    this.bankMethod.properties.forEach((prop) =>
    {
      if (this.isObjectField(prop) && prop.properties)
      {
        const subGroup = new FormGroup({});

        Object.entries(prop.properties).forEach(([subKey, subProp]) =>
        {
          const isSubRequired = prop.requiredFields?.includes(subKey);
          const subValidators = [];

          if (isSubRequired) { subValidators.push(Validators.required); }
          if (subProp.maxLength) { subValidators.push(Validators.maxLength(subProp.maxLength)); }
          if (subProp.minLength) { subValidators.push(Validators.minLength(subProp.minLength)); }

          subGroup.addControl(subKey, new FormControl('', subValidators));
        });

        group.addControl(prop.propertyName, subGroup);
      }
      else
      {
        const isRequired = this.bankMethod.requiredFields?.includes(prop.propertyName) ?? false;
        const validators = [];

        if (isRequired) { validators.push(Validators.required); }
        if (prop.pattern) { validators.push(Validators.pattern(prop.pattern)); }
        if (prop.maxLength) { validators.push(Validators.maxLength(prop.maxLength)); }
        if (prop.minLength) { validators.push(Validators.minLength(prop.minLength)); }

        const control = new FormControl(this.getPrefillValue(prop), validators);

        group.addControl(prop.propertyName, control);

        if (this.usesDatePicker(prop))
        {
          this.addDatePicker(prop.propertyName, control);
        }
      }
    });
  }

  private resetDatePickers()
  {
    this.datePickersRebuilt$.next();
    this.datePickerPropertyNames.clear();

    Object.keys(this.datePickerGroup.controls).forEach(key => this.datePickerGroup.removeControl(key));
  }

  private addDatePicker(propertyName: string, fieldControl: FormControl)
  {
    this.datePickerPropertyNames.add(propertyName);

    const pickerControl = new FormControl(this.parseIsoDate(fieldControl.value));

    this.datePickerGroup.addControl(propertyName, pickerControl);

    pickerControl.valueChanges
      .pipe(takeUntil(this.datePickersRebuilt$), takeUntil(this.destroy$))
      .subscribe((date: Date) =>
      {
        // min/max only constrain the calendar; a typed out-of-range date still parses to a valid
        // Date. The picker group sits outside bankForm, so nothing else would stop it reaching Nmible.
        fieldControl.setValue(pickerControl.valid ? this.formatIsoDate(date) : '');
        fieldControl.markAsTouched();
      });
  }

  // The picker writes into datePickerGroup, so blurring it never marks the Nmible field control
  // touched by itself - without this the required error would never surface.
  onDatePickerTouched(propertyName: string)
  {
    this.accountFieldsGroup.get(propertyName)?.markAsTouched();
  }

  // Material reports date problems on the picker control, while `required` sits on the Nmible field
  // control. Both have to be read, otherwise an out-of-range date - which clears the field - would
  // be reported as a missing value instead of a bad one.
  datePickerErrorFor(propertyName: string): string
  {
    const pickerControl = this.datePickerGroup.get(propertyName);

    if (pickerControl == null || !pickerControl.touched)
    {
      return '';
    }

    if (pickerControl.hasError('matDatepickerParse'))
    {
      return 'is not a valid date';
    }

    if (pickerControl.hasError('matDatepickerMin') || pickerControl.hasError('matDatepickerMax'))
    {
      return 'must be between 01/01/1900 and today';
    }

    return '';
  }

  private usesDatePicker(prop: NmiblePaymentMethodProperty): boolean
  {
    const propertyName = (prop.propertyName ?? '').toLowerCase();

    if (PaymentInfoNmibleBankComponent.DATE_OF_BIRTH_PROPERTY_NAMES.indexOf(propertyName) === -1)
    {
      return false;
    }

    return this.acceptsIsoDate(prop);
  }

  // The picker can only produce YYYY-MM-DD, so the plain text input stays in place whenever the
  // field's own Nmible validation would reject that format.
  private acceptsIsoDate(prop: NmiblePaymentMethodProperty): boolean
  {
    const probe = PaymentInfoNmibleBankComponent.ISO_DATE_PROBE;

    if ((prop.minLength && probe.length < prop.minLength) || (prop.maxLength && probe.length > prop.maxLength))
    {
      return false;
    }

    if (!prop.pattern)
    {
      return true;
    }

    // Mirrors how Validators.pattern anchors a string pattern.
    try
    {
      return new RegExp('^(?:' + prop.pattern.replace(/^\^/, '').replace(/\$$/, '') + ')$').test(probe);
    }
    catch
    {
      return false;
    }
  }

  private formatIsoDate(date: Date): string
  {
    if (!(date instanceof Date) || isNaN(date.getTime()))
    {
      return '';
    }

    const pad = (value: number) => ('0' + value).slice(-2);

    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  private parseIsoDate(value: any): Date | null
  {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof value === 'string' ? value : '');

    if (!match)
    {
      return null;
    }

    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  loadBankFields(country: string)
  {
    this.loadingMethods = true;
    this.bankMethod = null;

    const group = this.accountFieldsGroup;
    Object.keys(group.controls).forEach(key => group.removeControl(key));
    this.resetDatePickers();

    this.patientService.getNmibleBankAccountMethods(country).pipe(takeUntil(this.destroy$)).subscribe({
      next: (methods) =>
      {
        this.bankMethods = methods?.filter(Boolean) ?? [];
        this.bankMethod = this.bankMethods[0] ?? null;

        if (this.bankMethod)
        {
          this.bankForm.patchValue({ provider: this.bankMethod.provider }, { emitEvent: false });

          this.buildAccountFields();

          this.bankForm.patchValue(
            {
              givenName: this.patientDetail?.firstName,
              middleName: '',
              familyName: this.patientDetail?.lastName,
              email: this.patientDetail?.email
            },
            { emitEvent: false });
        }

        this.loadingMethods = false;
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.loadingMethods = false;
        this.alertService.showWarningAlert('Failed to load bank account fields for this country.');
      }
    });
  }

  getProviderOptions(): SelectOption[]
  {
    return this.bankMethods.map(m => new SelectOption(
      m.provider,
      m.provider.charAt(0).toUpperCase() + m.provider.slice(1).toLowerCase()
    ));
  }

  isRequiredField(propertyName: string): boolean
  {
    return this.bankMethod?.requiredFields?.includes(propertyName) ?? false;
  }

  isEnumField(prop: NmiblePaymentMethodProperty): boolean
  {
    return (prop.enum?.length ?? 0) > 0;
  }

  isObjectField(prop: NmiblePaymentMethodProperty): boolean
  {
    return prop.type === 'object';
  }

  getEnumOptions(prop: NmiblePaymentMethodProperty): SelectOption[]
  {
    return (prop.enum ?? []).map(e => new SelectOption(e.key, e.value));
  }

  getSubFieldKeys(prop: NmiblePaymentMethodProperty): string[]
  {
    if (!prop.properties) { return []; }

    return Object.entries(prop.properties)
      .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
      .map(([key]) => key);
  }

  getSubProperty(prop: NmiblePaymentMethodProperty, key: string): NmiblePaymentMethodSubProperty
  {
    return prop.properties?.[key];
  }

  isRequiredSubField(prop: NmiblePaymentMethodProperty, key: string): boolean
  {
    return prop.requiredFields?.includes(key) ?? false;
  }

  isEnumSubField(subProp: NmiblePaymentMethodSubProperty): boolean
  {
    return (subProp?.options?.length ?? 0) > 0;
  }

  getSubFieldOptions(subProp: NmiblePaymentMethodSubProperty): SelectOption[]
  {
    return (subProp?.options ?? []).map(o => new SelectOption(o.key, o.value));
  }

  getSubFieldGroup(prop: NmiblePaymentMethodProperty): FormGroup
  {
    return this.accountFieldsGroup.get(prop.propertyName) as FormGroup;
  }

  createBankAccount()
  {
    const formValue = this.bankForm.getRawValue();
    const request: CreateNmibleBankAccountRequest = {
      patientTrialId: this.patientTrialId,
      country: formValue.country,
      provider: formValue.provider,
      givenName: formValue.givenName,
      middleName: formValue.middleName || undefined,
      familyName: formValue.familyName,
      email: formValue.email,
      accountFields: formValue.accountFields as { [key: string]: any }
    };

    this.processing = true;
    this.patientService.createNmibleBankAccount(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: () =>
      {
        this.processing = false;
        this.alertService.showSuccessAlert('Bank account created successfully.');
        this.loadBankAccounts();
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showWarningAlert('Failed to save bank account. Please try again or contact support.');
      }
    });
  }

  onSourcePatientSelected(option: SelectOption)
  {
    this.sourcePatientName = option?.text ?? '';
    this.sourceAccounts = [];
    this.sourceTrialOptions = [];
    this.copySourceForm.get('sourceTrialId').setValue('');
    this.selectedSourceAccount = null;
    this.sourceBankDetails = null;

    if (!option?.value)
    {
      return;
    }

    this.loadingSourceAccounts = true;

    this.patientService.getNmibleBankAccountsForPatient(option.value).pipe(takeUntil(this.destroy$)).subscribe({
      next: (accounts: NmibleBankAccountSummary[]) =>
      {
        this.loadingSourceAccounts = false;
        this.sourceAccounts = accounts ?? [];

        if (this.sourceAccounts.length === 0)
        {
          this.alertService.showWarningAlert('This patient does not have a Nmible bank account.');
          return;
        }

        const selectedAccounts = this.sourceAccounts.filter(a => a.selected);

        if (selectedAccounts.length === 0)
        {
          this.alertService.showWarningAlert('This patient does not have a selected Nmible bank account.');
          return;
        }

        // Multi-trial patient with more than one selected account - let the operator pick instead of guessing.
        if (selectedAccounts.length > 1)
        {
          this.sourceTrialOptions = selectedAccounts.map(a => ({ value: a.patientTrialId, text: a.trialCode ?? a.trialName }));
          return;
        }

        this.selectSourceAccount(selectedAccounts[0]);
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.loadingSourceAccounts = false;
        this.alertService.showWarningAlert('Failed to load bank accounts for the selected patient.');
      }
    });
  }

  onSourceTrialSelected(patientTrialId: string)
  {
    const account = this.sourceAccounts.find(a => a.patientTrialId === patientTrialId);

    if (account)
    {
      this.selectSourceAccount(account);
    }
  }

  private selectSourceAccount(account: NmibleBankAccountSummary)
  {
    if (!account)
    {
      return;
    }

    this.selectedSourceAccount = account;
    this.loadingSourceAccounts = true;

    this.patientService.getNmibleBankAccountDetails(account.patientTrialId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (details: NmibleBankAccountDetailsResponse) =>
      {
        this.loadingSourceAccounts = false;
        this.sourceBankDetails = details;
        this.splitSourceFields(details.fields ?? {});

        if (this.isAlreadyAssignedToTarget(details))
        {
          this.alertService.showWarningAlert('This bank account has already been assigned to this patient.');
          return;
        }

        this.loadSourceBankMethod(details.country, this.sourceIdentityFields['_provider']);
        this.assignBankDetailsModal?.show();
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.loadingSourceAccounts = false;
        this.alertService.showWarningAlert('Failed to load bank details for the selected patient.');
      }
    });
  }

  private isAlreadyAssignedToTarget(details: NmibleBankAccountDetailsResponse): boolean
  {
    const sourceAccountFields = this.splitFields(details.fields ?? {}).accountFields;

    return this.bankAccounts.some(existing =>
      existing.country === details.country && this.accountFieldsMatch(sourceAccountFields, this.splitFields(existing.fields ?? {}).accountFields));
  }

  private accountFieldsMatch(a: { [key: string]: any }, b: { [key: string]: any }): boolean
  {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length)
    {
      return false;
    }

    return aKeys.every(key => JSON.stringify(a[key]) === JSON.stringify(b[key]));
  }

  private splitFields(fields: { [key: string]: any }): { identityFields: { [key: string]: any }; accountFields: { [key: string]: any } }
  {
    const identityKeys = new Set(['givenName', 'middleName', 'familyName', 'email', '_provider']);
    const identityFields: { [key: string]: any } = {};
    const accountFields: { [key: string]: any } = {};

    for (const key of Object.keys(fields))
    {
      if (identityKeys.has(key))
      {
        identityFields[key] = fields[key];
      }
      else
      {
        accountFields[key] = fields[key];
      }
    }

    return { identityFields, accountFields };
  }

  private splitSourceFields(fields: { [key: string]: any })
  {
    const split = this.splitFields(fields);
    this.sourceIdentityFields = split.identityFields;
    this.sourceAccountFields = split.accountFields;
  }

  private resolveBankMethod(country: string, provider: string): Observable<NmibleGetPaymentMethodsResponse | null>
  {
    return this.patientService.getNmibleBankAccountMethods(country).pipe(
      map((methods) =>
      {
        const bankMethods = methods?.filter(Boolean) ?? [];
        return provider
          ? (bankMethods.find(m => m.provider === provider || m.methodProviderName === provider) ?? bankMethods[0] ?? null)
          : (bankMethods[0] ?? null);
      })
    );
  }

  private loadSourceBankMethod(country: string, provider: string)
  {
    this.resolveBankMethod(country, provider).pipe(takeUntil(this.destroy$)).subscribe({
      next: (method) =>
      {
        this.sourceBankMethod = method;
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.sourceBankMethod = null;
      }
    });
  }

  private getFieldTitle(bankMethod: NmibleGetPaymentMethodsResponse | null, propertyName: string): string
  {
    const prop = bankMethod?.properties?.find(p => p.propertyName === propertyName);
    return prop?.title ?? propertyName;
  }

  getSourceFieldTitle(propertyName: string): string
  {
    return this.getFieldTitle(this.sourceBankMethod, propertyName);
  }

  cancelAssignBankDetails()
  {
    this.assignBankDetailsModal?.hide();
  }

  confirmAssignBankDetails()
  {
    if (!this.sourceBankDetails)
    {
      return;
    }

    const request: CreateNmibleBankAccountRequest = {
      patientTrialId: this.patientTrialId,
      country: this.sourceBankDetails.country,
      provider: this.sourceIdentityFields['_provider'] || undefined,
      givenName: this.sourceIdentityFields['givenName'] || this.patientDetail?.firstName,
      middleName: this.sourceIdentityFields['middleName'] || undefined,
      familyName: this.sourceIdentityFields['familyName'] || this.patientDetail?.lastName,
      email: this.sourceIdentityFields['email'] || this.patientDetail?.email,
      accountFields: this.sourceAccountFields
    };

    this.processing = true;
    this.patientService.createNmibleBankAccount(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: () =>
      {
        this.processing = false;
        this.assignBankDetailsModal?.hide();
        this.alertService.showSuccessAlert('Bank account details copied successfully.');
        this.loadBankAccounts();
      },
      error: (err) =>
      {
        LogHelper.error(err);
        this.processing = false;
        this.alertService.showWarningAlert('Failed to assign bank details. Please try again or contact support.');
      }
    });
  }
}
